import { LocationOnRounded } from '@mui/icons-material';
import { CircularProgress, InputAdornment, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import DirectInteractionActionCreator from '../../actions/DirectInteractionActionCreator';
import Config from '../../config';
import { IPaginatedCollection, emptyPaginatedCollection } from '../../models/collections/IPaginatedCollection';
import { IError, isError } from '../../models/core/error/IError';
import { ILocation } from '../../models/location/ILocation';
import BerndMapper from '../../temp/BerndMapper';

export interface ILocationAutocompleteProps {
    placeholder: string;
    placeholderTextColor?: string;
    countryCode?: string;
    style?: React.CSSProperties;
    value?: string;
    preselectedZip?: string;
    listStyle?: React.CSSProperties;
    hideZip?: boolean;
    inputStyle?: React.CSSProperties;
    adornmentStyle?: React.CSSProperties;
    showAdornment?: boolean;
    onChange?(text: string, zip: string): void;
    onLocationMatched(city: string, postalCode: string): void;
}

export const LocationAutocomplete = (props: ILocationAutocompleteProps) => {
    const { placeholder, value, onLocationMatched, preselectedZip, hideZip, inputStyle, adornmentStyle, showAdornment, countryCode = 'DE' } = props;
    const [locations, setLocations] = useState<IPaginatedCollection<ILocation>>(emptyPaginatedCollection());
    const [isLoading, setLoading] = useState(false);
    const debounceTimer = useRef<number | undefined>();

    useEffect(() => {
        if (value && preselectedZip) {
            const loc: ILocation = { City: value, Zip: preselectedZip, countryCode: countryCode, id: 0 };
            setLocations(BerndMapper.mapToPaginatedCollection([loc]));
        }
    }, [value, preselectedZip, countryCode]);

    useEffect(() => {
        setLocations(emptyPaginatedCollection());
    }, [countryCode]);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const text = e.currentTarget.value;
            if (text === '') {
                // setLocations(emptyPaginatedCollection());
            }

            if (text !== '') {
                setLoading(true);
            }

            if (text) {
                if (debounceTimer.current) {
                    window.clearTimeout(debounceTimer.current);
                }

                debounceTimer.current = window.setTimeout(async () => {
                    setLoading(true);

                    let city: string | undefined = undefined;
                    let postalCode: string | undefined = undefined;

                    if (/([0-9])+/g.test(text)) {
                        postalCode = text;
                    } else {
                        city = text;
                    }

                    try {
                        const response: IError | ILocation[] = await (
                            await DirectInteractionActionCreator.getMatchingLocation(postalCode, city, countryCode)
                        ).json();

                        if (response && !isError(response)) {
                            setLocations(BerndMapper.mapToPaginatedCollection(response));
                        }
                    } catch {
                        // Network error or a non-JSON response — surface as "no
                        // results" rather than leaving the spinner stuck forever.
                        setLocations(emptyPaginatedCollection());
                    } finally {
                        debounceTimer.current = undefined;
                        setLoading(false);
                    }
                }, 1000);
            }
        },
        [countryCode]
    );

    const handleSelect = useCallback(
        (_: React.SyntheticEvent, location: ILocation | null) => {
            if (location) {
                onLocationMatched(location.City, location.Zip);
            }
        },
        [onLocationMatched]
    );

    return (
        <Autocomplete
            key={countryCode}
            options={locations._embedded.items}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={placeholder}
                    onChange={handleChange}
                    sx={{
                        "& .MuiInputBase-root": {
                            height: 55,
                        },
                    }}
                    InputProps={{
                        ...params.InputProps,
                        style: inputStyle,
                        startAdornment: showAdornment ? (
                            <InputAdornment position="start" style={adornmentStyle}>
                                <LocationOnRounded />
                            </InputAdornment>
                        ) : undefined,
                        endAdornment: (
                            <React.Fragment>
                                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}
            getOptionLabel={(item: ILocation) => (hideZip ? item.City : `${item.Zip}, ${item.City}`)}
            isOptionEqualToValue={(option: ILocation, value: ILocation) => option.City === value.City}
            onChange={handleSelect}
            filterOptions={(x: ILocation[]) => x}
            defaultValue={value && preselectedZip ? { City: value, Zip: preselectedZip, countryCode: countryCode, id: 1 } : undefined}
            loading={isLoading}
            disableClearable
            forcePopupIcon={false}
            slotProps={{
                popper: {
                    // The dialog that hosts this field is forced to the max z-index,
                    // so the options popper must match it to render above the dialog.
                    style: { zIndex: 2147483647 },
                },
                paper: {
                    sx: {
                        mt: 1,
                        borderRadius: '16px',
                        overflow: 'hidden',
                        border: '1px solid rgba(20, 20, 30, 0.06)',
                        boxShadow: '0 12px 28px rgba(20, 20, 40, 0.18)',
                        '& .MuiAutocomplete-listbox': {
                            p: 1,
                            maxHeight: 280,
                            '&::-webkit-scrollbar': { width: 6 },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: 'rgba(20, 20, 30, 0.18)',
                                borderRadius: 8,
                            },
                        },
                        '& .MuiAutocomplete-option': {
                            borderRadius: '10px',
                            minHeight: 'auto',
                            px: 1.5,
                            py: 1,
                            fontSize: '15px',
                            fontWeight: 600,
                            color: '#1f2330',
                            transition: 'background-color 120ms ease, color 120ms ease',
                            '&:hover, &.Mui-focused': {
                                backgroundColor: `${Config.GLOBAL_PRIMARY_COLOR}14`,
                                color: Config.GLOBAL_PRIMARY_COLOR,
                            },
                            '&[aria-selected="true"]': {
                                backgroundColor: `${Config.GLOBAL_PRIMARY_COLOR}24`,
                                color: Config.GLOBAL_PRIMARY_COLOR,
                            },
                            '&[aria-selected="true"].Mui-focused': {
                                backgroundColor: `${Config.GLOBAL_PRIMARY_COLOR}2e`,
                            },
                        },
                        '& .MuiAutocomplete-noOptions, & .MuiAutocomplete-loading': {
                            fontSize: '14px',
                            fontWeight: 600,
                            color: '#6b6e7a',
                        },
                    },
                },
            }}
        />
    );
};

export default LocationAutocomplete;
